import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERMISSION, can, formatIst, inboxApi, useAuth,
  type CampaignDetail, type CampaignRecipient, type RecipientAction,
} from '@wa/shared';
import { campaignNote } from '@/campaigns/CampaignStatusChip';
import { outcomeLabel } from '@/campaigns/CampaignBuilder';
import { ListTargetDialog, saveCsv, type ListTarget } from '@/broadcast/ListTargetDialog';

export type Outcome =
  | 'all' | 'delivered' | 'unread' | 'read' | 'clicked' | 'replied' | 'noresponse' | 'failed' | 'skipped' | 'pending';

const OUTCOMES: readonly Outcome[] = [
  'all', 'failed', 'skipped', 'delivered', 'unread', 'read', 'clicked', 'replied', 'noresponse', 'pending',
];

const OUTCOME_LABEL: Record<Outcome, string> = {
  all: 'Everyone', delivered: 'Delivered', unread: 'Unread', read: 'Read', clicked: 'Clicked',
  replied: 'Replied', noresponse: 'No response', failed: 'Failed', skipped: 'Skipped', pending: 'Pending',
};

/**
 * What happened, once it has started happening.
 *
 * The numbers along the top are the summary; the list underneath exists for one
 * question — "which ones did not work, and why" — so it opens filtered rather than
 * making somebody scroll twenty thousand successes to find forty failures.
 */
export function CampaignReport({ campaign, initialOutcome = 'all', onClose }: {
  campaign: CampaignDetail;
  /** The line that was tapped to open this — "Read by" opens on Read. */
  initialOutcome?: Outcome;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Opening a chat from a row needs the inbox. The API still decides whether this
  // person may see that particular conversation; this only hides a dead link.
  const mayOpenChats = can(user, PERMISSION.inboxView);
  const mayEditLists = can(user, PERMISSION.segmentsWrite);
  const mayFlag = can(user, PERMISSION.contactsWrite);
  const mayExport = can(user, PERMISSION.contactsExport);
  const [outcome, setOutcome] = useState<Outcome>(initialOutcome);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The selection on the recipient list. "All" is server-side: everyone under
  // this outcome, not the hundred rows loaded — a report line's number is the
  // whole set and the bar must act on the whole set.
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [addingTo, setAddingTo] = useState(false);
  const changeOutcome = (next: Outcome) => { setOutcome(next); setPicked(new Set()); setAllMatching(false); };

  const detail = useQuery({
    queryKey: ['campaign', campaign.id],
    queryFn: () => inboxApi.campaign(campaign.id),
    // Only while it is moving. A finished campaign is a static document, and
    // polling one forever is a request every few seconds that can never change.
    refetchInterval: campaign.status === 2 ? 3000 : false,
    initialData: { campaign, clicks: [] },
  });

  const current = detail.data?.campaign ?? campaign;
  const clicks = detail.data?.clicks ?? [];

  const recipients = useQuery({
    queryKey: ['campaign-recipients', campaign.id, outcome],
    queryFn: () => inboxApi.campaignRecipients(campaign.id, outcome === 'all' ? null : outcome),
  });

  const act = (fn: () => Promise<unknown>, fallback: string) => async () => {
    setError(null);
    try {
      await fn();
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : fallback);
    }
  };

  const bulk = useMutation({
    mutationFn: (body: { action: RecipientAction } & Partial<ListTarget>) => inboxApi.recipientActions(campaign.id, {
      action: body.action,
      ...(allMatching ? { all: true, outcome: outcome === 'all' ? null : outcome } : { recipientIds: [...picked] }),
      targetSegmentId: 'targetSegmentId' in body ? body.targetSegmentId : null,
      newListName: 'newListName' in body ? body.newListName : null,
    }),
    onSuccess: (result, body) => {
      setError(null);
      setAddingTo(false);
      setPicked(new Set());
      setAllMatching(false);
      const n = result.affected.toLocaleString('en-IN');
      setNotice(
        body.action === 'removeFromList' ? `${n} removed from the list.`
          : body.action === 'addToList' ? `${n} added to the list.`
            : `${n} marked as not on WhatsApp — they are left out of every list from now on.`,
      );
      setTimeout(() => setNotice(null), 5000);
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
      void queryClient.invalidateQueries({ queryKey: ['segment-members'] });
      void queryClient.invalidateQueries({ queryKey: ['campaign-recipients', campaign.id] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'That did not work.'),
  });

  const exportCsv = async () => {
    try {
      const { blob, fileName } = await inboxApi.exportRecipients(campaign.id, outcome);
      saveCsv(blob, fileName || `${campaign.name} — ${OUTCOME_LABEL[outcome]}.csv`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not export.');
    }
  };

  const anySelected = allMatching || picked.size > 0;

  const cancel = useMutation({
    mutationFn: () => inboxApi.cancelCampaign(campaign.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not cancel that.'),
  });

  const note = campaignNote(current);
  const percent = current.totalRecipients > 0
    ? Math.round(((current.sentCount + current.failedCount + current.skippedCount) / current.totalRecipients) * 100)
    : 0;

  return (
    <div className="pane-scroll h-full overflow-y-auto">
      <div className="mx-auto flex max-w-3xl flex-col gap-5 p-3 sm:p-5">
        {error && (
          <p role="alert" className="rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="rounded-(--radius-field) border border-(--color-good) px-3 py-2 text-small text-(--color-good)">
            {notice}
          </p>
        )}

        {note && (
          <p className="rounded-(--radius-panel) border border-(--color-warn) bg-(--color-warn)/8 px-3 py-2.5 text-small leading-relaxed text-(--color-ink-soft)">
            {note}
          </p>
        )}

        {current.retargetOfCampaignId && (
          <p className="text-small text-(--color-muted)">
            Sent to everyone from an earlier campaign whose message was {outcomeLabel(current.retargetOutcome)}.
          </p>
        )}

        <section>
          <div className="flex items-baseline justify-between">
            <h3 className="text-small font-semibold">Progress</h3>
            <span className="text-micro tabular-nums text-(--color-muted)">
              {(current.sentCount + current.failedCount + current.skippedCount).toLocaleString('en-IN')} of{' '}
              {current.totalRecipients.toLocaleString('en-IN')}
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-(--color-sunken)">
            <div
              className={`h-full rounded-full transition-[width] duration-500 ${
                current.status === 3 ? 'bg-(--color-warn)' : 'bg-(--color-brand)'
              }`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </section>

        {/* Each tile is the same question as the chip below it — tapping "Failed"
            shows the failures. Sent has no narrower list than everyone. */}
        <dl className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          <Stat label="Sent" value={current.sentCount} active={outcome === 'all'} onClick={() => changeOutcome('all')} />
          <Stat label="Delivered" value={current.deliveredCount} active={outcome === 'delivered'} onClick={() => changeOutcome('delivered')} />
          <Stat label="Read" value={current.readCount} active={outcome === 'read'} onClick={() => changeOutcome('read')} />
          <Stat label="Clicked" value={current.clickedCount ?? 0} active={outcome === 'clicked'} onClick={() => changeOutcome('clicked')} />
          <Stat label="Replied" value={current.repliedCount ?? 0} active={outcome === 'replied'} onClick={() => changeOutcome('replied')} />
          <Stat label="Failed" value={current.failedCount} tone={current.failedCount > 0 ? 'bad' : undefined} active={outcome === 'failed'} onClick={() => changeOutcome('failed')} />
          <Stat label="Skipped" value={current.skippedCount} active={outcome === 'skipped'} onClick={() => changeOutcome('skipped')} />
        </dl>

        <p className="text-micro leading-relaxed text-(--color-muted)">
          Skipped means the message was never sent — almost always because the person
          opted out between the list being drawn up and their turn, or because the
          campaign was cancelled before their turn. Failed means WhatsApp refused it,
          either straight away or in a receipt after accepting it — the reason is on
          each row. Replied means something came in on that chat after the broadcast
          reached them — a tap on one of its buttons counts as Clicked, not Replied.
        </p>

        {clicks.length > 0 && (
          <section>
            <h3 className="mb-2 text-small font-semibold">Buttons tapped</h3>
            <ul className="flex flex-col divide-y divide-(--color-line-dim) rounded-(--radius-panel) border border-(--color-line)">
              {clicks.map((click) => (
                <li key={click.buttonPayload} className="flex items-center gap-3 px-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-small">
                    {click.buttonText ?? click.buttonPayload}
                  </span>
                  <span className="shrink-0 text-small font-medium tabular-nums">
                    {click.clicks.toLocaleString('en-IN')}
                  </span>
                  <span className="w-12 shrink-0 text-right text-micro text-(--color-muted)">
                    {current.deliveredCount > 0
                      ? `${Math.round((click.clicks / current.deliveredCount) * 100)}%`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-micro text-(--color-muted)">
              People, not taps — somebody tapping the same button twice is one.
            </p>
          </section>
        )}

        <section>
          {/* No negative margin. The usual -mx-1/px-1 bleed makes the row wider than
              its container, and with nothing clipping it the chips sit over the panel
              edge — which the layout check caught at every width above 768. */}
          <div className="chip-scroll mb-2 flex gap-1 overflow-x-auto">
            {OUTCOMES.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => changeOutcome(value)}
                className={[
                  'tap shrink-0 rounded-full px-2.5 py-1 text-micro font-medium transition',
                  outcome === value
                    ? 'bg-(--color-brand-wash) text-(--color-brand)'
                    : 'text-(--color-muted) hover:bg-(--color-sunken)',
                ].join(' ')}
              >
                {OUTCOME_LABEL[value]}
              </button>
            ))}
          </div>

          {/* Act on the people this line shows. The report is where an outcome
              lives, so this is where "drop everyone who failed from the list"
              belongs — not on the members panel, which knows nothing of sends. */}
          {(recipients.data?.items.length ?? 0) > 0 && (mayEditLists || mayFlag || mayExport) && (
            <div className={`mb-2 flex flex-wrap items-center gap-1.5 rounded-(--radius-field) px-2.5 py-1.5 ${anySelected ? 'bg-(--color-brand-wash)' : 'bg-(--color-sunken)'}`}>
              <label className="flex items-center gap-1.5 text-small">
                <input
                  type="checkbox"
                  checked={allMatching}
                  onChange={(e) => { setAllMatching(e.target.checked); setPicked(new Set()); }}
                  className="size-4 accent-(--color-brand)"
                />
                {allMatching
                  ? `Everyone under ${OUTCOME_LABEL[outcome]}`
                  : picked.size > 0 ? `${picked.size} selected` : `Select everyone under ${OUTCOME_LABEL[outcome]}`}
              </label>
              <span className="mr-auto" />
              {anySelected && mayEditLists && current.segmentId && (
                <Act
                  label="Remove from list" tone="bad" busy={bulk.isPending}
                  onClick={() => { if (window.confirm(allMatching ? `Remove everyone under ${OUTCOME_LABEL[outcome]} from the list this went to?` : `Remove ${picked.size} from the list?`)) bulk.mutate({ action: 'removeFromList' }); }}
                />
              )}
              {anySelected && mayEditLists && <Act label="Add to list…" busy={bulk.isPending} onClick={() => setAddingTo(true)} />}
              {anySelected && mayFlag && (outcome === 'failed' || outcome === 'all') && (
                <Act
                  label="Not on WhatsApp" busy={bulk.isPending}
                  title="Marks these numbers as unreachable so every list leaves them out until they write in or a message is delivered."
                  onClick={() => { if (window.confirm('Mark these numbers as not on WhatsApp? Every list leaves them out until a reply or a delivery proves otherwise.')) bulk.mutate({ action: 'markNoWhatsapp' }); }}
                />
              )}
              {mayExport && <Act label="CSV" busy={false} title="Download everyone under this outcome" onClick={() => void exportCsv()} />}
            </div>
          )}

          {recipients.isPending ? (
            <p className="py-4 text-small text-(--color-muted)">Loading…</p>
          ) : (recipients.data?.items.length ?? 0) === 0 ? (
            <p className="rounded-(--radius-panel) border border-(--color-line) px-3 py-6 text-center text-small text-(--color-muted)">
              {outcome === 'failed' ? 'Nothing failed.' : 'Nobody here.'}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-(--color-line-dim) rounded-(--radius-panel) border border-(--color-line)">
              {recipients.data!.items.map((r) => (
                <RecipientRow
                  key={r.id}
                  recipient={r}
                  checked={allMatching || picked.has(r.id)}
                  onToggle={(mayEditLists || mayFlag) ? () => {
                    if (allMatching) { setAllMatching(false); setPicked(new Set(recipients.data!.items.filter((x) => x.id !== r.id).map((x) => x.id))); return; }
                    setPicked((prev) => { const next = new Set(prev); if (next.has(r.id)) next.delete(r.id); else next.add(r.id); return next; });
                  } : undefined}
                  onOpen={mayOpenChats && r.conversationId ? () => navigate(`/chats?open=${r.conversationId}`) : undefined}
                />
              ))}
            </ul>
          )}
        </section>

        {addingTo && (
          <ListTargetDialog
            title="Add to which list?"
            subtitle={allMatching ? `Everyone under ${OUTCOME_LABEL[outcome]}.` : `${picked.size} ${picked.size === 1 ? 'person' : 'people'}.`}
            excludeId={current.segmentId}
            busy={bulk.isPending}
            error={bulk.isError ? error : null}
            onPick={(t) => bulk.mutate({ action: 'addToList', ...t })}
            onClose={() => setAddingTo(false)}
          />
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-(--color-line) pt-4">
          {current.status === 2 && (
            <button
              type="button"
              onClick={act(() => inboxApi.pauseCampaign(campaign.id), 'Could not pause that.')}
              className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small"
            >
              Pause
            </button>
          )}

          {current.status === 3 && (
            <button
              type="button"
              onClick={act(() => inboxApi.resumeCampaign(campaign.id), 'Could not resume that.')}
              className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-2 text-small font-medium text-white"
            >
              Resume
            </button>
          )}

          {[1, 2, 3].includes(current.status) && (
            <button
              type="button"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate()}
              className="tap ml-auto rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small text-(--color-bad) disabled:opacity-40"
            >
              Cancel the rest
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small"
          >
            Close
          </button>
        </div>

        {[1, 2, 3].includes(current.status) && (
          <p className="text-micro leading-relaxed text-(--color-muted)">
            Cancelling stops what has not gone yet. Messages already accepted by
            WhatsApp cannot be recalled.
          </p>
        )}
      </div>
    </div>
  );
}

function Act({ label, tone, busy, title, onClick }: { label: string; tone?: 'bad'; busy: boolean; title?: string; onClick: () => void }) {
  return (
    <button
      type="button" disabled={busy} onClick={onClick} title={title}
      className={`tap rounded-(--radius-field) border px-2.5 py-1 text-micro font-medium disabled:opacity-50 ${tone === 'bad' ? 'border-(--color-bad)/40 text-(--color-bad) hover:bg-(--color-bad)/8' : 'border-(--color-line) bg-(--color-surface) hover:bg-(--color-sunken)'}`}
    >
      {label}
    </button>
  );
}

function RecipientRow({ recipient, checked, onToggle, onOpen }: {
  recipient: CampaignRecipient; checked: boolean; onToggle?: () => void; onOpen?: () => void;
}) {
  // The message's own status outranks the recipient row once there is one: the
  // dispatcher records "sent", and delivery and read arrive later as receipts.
  const label =
    recipient.status === 4 ? 'Skipped'
      : recipient.status === 3 ? 'Failed'
        : recipient.messageStatus === 3 ? 'Read'
          : recipient.messageStatus === 2 ? 'Delivered'
            : recipient.messageStatus === 4 ? 'Failed'
              : recipient.status === 2 ? 'Sent'
                : 'Waiting';

  const bad = label === 'Failed' || label === 'Skipped';
  const reason = recipient.failureReason ?? recipient.errorTitle;

  const body = (
    <>
      <div className="min-w-0 flex-1">
        <p className="truncate text-small">
          {recipient.displayName ?? `+${recipient.waId ?? ''}`}
          {recipient.notOnWhatsAppAt && <span className="ml-1.5 rounded-full bg-(--color-bad)/10 px-1.5 py-0.5 text-micro font-medium text-(--color-bad)">not on WhatsApp</span>}
        </p>
        {reason && (
          <p className={`mt-0.5 text-micro leading-snug ${bad ? 'text-(--color-bad)' : 'text-(--color-muted)'}`}>
            {reason}
          </p>
        )}
        {recipient.repliedAt && (
          <p className="mt-0.5 text-micro leading-snug text-(--color-good)">
            Replied {formatIst(recipient.repliedAt)}
          </p>
        )}
      </div>
      <div className="shrink-0 text-right">
        <span className={`text-micro font-medium ${bad ? 'text-(--color-bad)' : 'text-(--color-muted)'}`}>
          {label}
        </span>
        {recipient.processedAt && (
          <p className="text-micro text-(--color-muted)">{formatIst(recipient.processedAt)}</p>
        )}
      </div>
    </>
  );

  // A row is a link to the chat when there is one to open. Skipped and never-sent
  // rows have no message and so no conversation; they stay plain.
  const box = onToggle && (
    <input
      type="checkbox" checked={checked} onChange={onToggle}
      aria-label={`Select ${recipient.displayName ?? recipient.waId ?? ''}`}
      className="mt-0.5 size-4 shrink-0 accent-(--color-brand)"
    />
  );
  const wash = checked ? 'bg-(--color-brand-wash)/60' : '';
  return onOpen ? (
    <li className={`flex items-start gap-2.5 px-3 py-2 ${wash}`}>
      {box}
      <button
        type="button"
        onClick={onOpen}
        title="Open the chat"
        className="tap flex min-w-0 flex-1 items-start gap-3 text-left hover:text-(--color-brand)"
      >
        {body}
      </button>
    </li>
  ) : (
    <li className={`flex items-start gap-2.5 px-3 py-2 ${wash}`}>
      {box}
      <div className="flex min-w-0 flex-1 items-start gap-3">{body}</div>
    </li>
  );
}

function Stat({ label, value, tone, active, onClick }: {
  label: string; value: number; tone?: 'bad'; active?: boolean; onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'tap rounded-(--radius-panel) border px-2 py-2 text-center transition',
        active ? 'border-(--color-brand) bg-(--color-brand-wash)' : 'border-(--color-line) hover:bg-(--color-sunken)',
      ].join(' ')}
    >
      <dd className={`text-title font-semibold tabular-nums ${tone === 'bad' ? 'text-(--color-bad)' : ''}`}>
        {value.toLocaleString('en-IN')}
      </dd>
      <dt className="text-micro text-(--color-muted)">{label}</dt>
    </button>
  );
}
